import { useEffect, useRef, useState } from "react";
import { Trash2, Pencil, Copy, Eye, X, ArrowUp, ArrowDown, Plus, CheckCircle2, FileText } from "lucide-react";
import ImageUpload from "../../components/ImageUpload";
import { thumb } from "../../lib/api";
import { Field } from "./Field";

const splitSteps = (v) => {
  const parts = String(v || "").split("\n");
  return parts.length === 1 && parts[0].trim() === "" ? [] : parts;
};

// Editable, reorderable list of written steps. Local state keeps freshly-added
// blank rows alive; the parent stores them as newline-joined text.
function StepsEditor({ value, onChange }) {
  const [steps, setSteps] = useState(() => splitSteps(value));
  const lastSynced = useRef(value);
  useEffect(() => {
    if (value !== lastSynced.current) {
      setSteps(splitSteps(value));
      lastSynced.current = value;
    }
  }, [value]);
  const commit = (arr) => {
    setSteps(arr);
    const joined = arr.join("\n");
    lastSynced.current = joined;
    onChange(joined);
  };
  const update = (i, v) => { const a = [...steps]; a[i] = v; commit(a); };
  const add = () => commit([...steps, ""]);
  const remove = (i) => commit(steps.filter((_, idx) => idx !== i));
  const move = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= steps.length) return;
    const a = [...steps];
    [a[i], a[j]] = [a[j], a[i]];
    commit(a);
  };
  return (
    <div>
      <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-1">Written steps (reorderable)</label>
      <div className="space-y-2">
        {steps.map((s, i) => (
          <div key={i} className="flex items-start gap-2" data-testid={`tut-step-row-${i}`}>
            <span className="mt-2 text-xs font-bold text-stone-400 w-5 text-right">{i + 1}.</span>
            <input
              data-testid={`tut-step-input-${i}`}
              value={s}
              onChange={(e) => update(i, e.target.value)}
              className="flex-1 px-3 py-2 rounded-lg border border-stone-200 focus:border-[#121212] focus:outline-none"
              placeholder={`Step ${i + 1}`}
            />
            <div className="flex gap-1">
              <button type="button" data-testid={`tut-step-up-${i}`} onClick={() => move(i, -1)} disabled={i === 0} className="p-2 text-stone-500 hover:text-[#121212] disabled:opacity-30"><ArrowUp size={15} /></button>
              <button type="button" data-testid={`tut-step-down-${i}`} onClick={() => move(i, 1)} disabled={i === steps.length - 1} className="p-2 text-stone-500 hover:text-[#121212] disabled:opacity-30"><ArrowDown size={15} /></button>
              <button type="button" data-testid={`tut-step-del-${i}`} onClick={() => remove(i)} className="p-2 text-stone-500 hover:text-red-500"><Trash2 size={15} /></button>
            </div>
          </div>
        ))}
      </div>
      <button type="button" data-testid="tut-step-add" onClick={add} className="mt-2 inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase font-bold text-[#121212] hover:text-[#E0218A]">
        <Plus size={14} /> Add step
      </button>
    </div>
  );
}

export default function AdminTutorials({ ctx }) {
  const { newTut, setNewTut, addTut, tutorials, delTut, products = [], editingTutId, editTut, cancelEditTut, toggleTutPublished, duplicateTut } = ctx;
  const kits = products.filter((p) => p.category === "cake_kit");
  const editing = !!editingTutId;
  const preview = (t) => window.open(`/tutorials/${t.slug || t.id}`, "_blank");

  return (
    <div className="mt-10 space-y-6">
      <div className="p-6 rounded-2xl bg-white border border-stone-200" data-testid="tutorial-form">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif font-black text-2xl text-[#121212]">{editing ? "Edit tutorial" : "Add a new tutorial"}</h3>
          {editing && (
            <button data-testid="cancel-edit-tut-btn" onClick={cancelEditTut} className="inline-flex items-center gap-1.5 text-xs tracking-[0.15em] uppercase font-bold text-stone-500 hover:text-[#121212]"><X size={14} /> Cancel edit</button>
          )}
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Title" value={newTut.title} onChange={(e) => setNewTut({ ...newTut, title: e.target.value })} />
          <Field label="Duration" value={newTut.duration} onChange={(e) => setNewTut({ ...newTut, duration: e.target.value })} />

          <div className="sm:col-span-2">
            <Field label="Vimeo URL (or paste the embed link)" value={newTut.video_url} onChange={(e) => setNewTut({ ...newTut, video_url: e.target.value })} placeholder="https://vimeo.com/123456789" data-testid="tutorial-vimeo-url" />
            <p className="text-xs text-[#121212]/55 mt-1">Paste a Vimeo link or embed code — it renders as a player on the tutorial page.</p>
          </div>

          <Field label="Price" type="number" value={newTut.price} onChange={(e) => setNewTut({ ...newTut, price: e.target.value })} />
          <Field label="Level" value={newTut.level} onChange={(e) => setNewTut({ ...newTut, level: e.target.value })} />

          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-1">Access</label>
            <select data-testid="tutorial-access-type" value={newTut.access_type} onChange={(e) => setNewTut({ ...newTut, access_type: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#121212] focus:outline-none">
              <option value="vault">Vault (included with membership)</option>
              <option value="premium">Premium (individual purchase)</option>
            </select>
          </div>

          <div>
            <label className="text-xs tracking-[0.2em] uppercase text-stone-500 block mb-1">Assigned kit (Shop This Kit)</label>
            <select data-testid="tutorial-kit-select" value={newTut.kit_id || ""} onChange={(e) => setNewTut({ ...newTut, kit_id: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-stone-200 focus:border-[#121212] focus:outline-none">
              <option value="">No kit assigned</option>
              {kits.map((p) => <option key={p.id} value={p.id}>{p.title}</option>)}
            </select>
          </div>

          <div className="sm:col-span-2">
            <Field label="Custom URL slug" value={newTut.slug || ""} onChange={(e) => setNewTut({ ...newTut, slug: e.target.value })} placeholder="auto-generated from title if left blank" data-testid="tutorial-slug" />
            <p className="text-xs text-[#121212]/55 mt-1">Public URL: /tutorials/{newTut.slug || "your-slug"}</p>
          </div>

          <div className="sm:col-span-2">
            <Field label="Description" type="textarea" value={newTut.description} onChange={(e) => setNewTut({ ...newTut, description: e.target.value })} />
          </div>
          <div className="sm:col-span-2">
            <Field label="Supplies & materials (one per line)" type="textarea" value={newTut.materials || ""} onChange={(e) => setNewTut({ ...newTut, materials: e.target.value })} data-testid="tutorial-materials" />
          </div>
          <div className="sm:col-span-2">
            <StepsEditor value={newTut.steps} onChange={(v) => setNewTut({ ...newTut, steps: v })} />
          </div>
          <div className="sm:col-span-2">
            <Field label="Troubleshooting (one per line)" type="textarea" value={newTut.troubleshooting || ""} onChange={(e) => setNewTut({ ...newTut, troubleshooting: e.target.value })} data-testid="tutorial-troubleshooting" />
          </div>

          <div className="sm:col-span-2">
            <ImageUpload library label="Cover image / thumbnail" testid="tutorial-image" value={newTut.image_url} onChange={(url) => setNewTut({ ...newTut, image_url: url })} />
          </div>

          <div className="sm:col-span-2 flex items-center gap-3">
            <input id="tut-published" data-testid="tutorial-published-toggle" type="checkbox" checked={newTut.published !== false} onChange={(e) => setNewTut({ ...newTut, published: e.target.checked })} className="w-5 h-5 accent-[#121212]" />
            <label htmlFor="tut-published" className="text-sm text-[#121212]">Published (uncheck to save as a draft)</label>
          </div>
        </div>

        <div className="mt-5 flex flex-wrap gap-3">
          <button data-testid="add-tutorial-btn" onClick={addTut} className="px-6 py-3 bg-[#121212] text-[#F2E9D8] hover:bg-black text-xs tracking-[0.25em] uppercase font-bold transition-all inline-flex items-center gap-2">
            <CheckCircle2 size={15} /> {editing ? "Save changes" : "Add tutorial"}
          </button>
          {editing && (
            <button data-testid="preview-edit-tut-btn" onClick={() => preview(newTut)} className="px-6 py-3 border-2 border-[#121212] text-[#121212] hover:bg-[#121212] hover:text-[#F2E9D8] text-xs tracking-[0.25em] uppercase font-bold transition-all inline-flex items-center gap-2">
              <Eye size={15} /> Preview
            </button>
          )}
        </div>
      </div>

      <div className="space-y-2">
        {tutorials.map((t) => (
          <div key={t.id} data-testid={`tut-row-${t.id}`} className="p-4 bg-white border border-stone-200 rounded-xl flex items-center justify-between gap-4">
            <div className="flex gap-3 items-center min-w-0">
              <img src={thumb(t.image_url, 120)} alt="" className="w-14 h-14 rounded-lg object-cover shrink-0" />
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <p className="font-medium text-stone-900 truncate">{t.title}</p>
                  <span className={`text-[10px] tracking-[0.15em] uppercase font-bold px-2 py-0.5 rounded ${t.published !== false ? "bg-[#C6FF33] text-[#121212]" : "bg-stone-200 text-stone-600"}`}>
                    {t.published !== false ? "Published" : "Draft"}
                  </span>
                </div>
                <p className="text-sm text-stone-500 truncate">{t.level} · ${t.price} · /tutorials/{t.slug || t.id}</p>
              </div>
            </div>
            <div className="flex items-center gap-1 shrink-0">
              <button title="Preview" data-testid={`preview-tut-${t.id}`} onClick={() => preview(t)} className="p-2 text-stone-500 hover:text-[#121212]"><Eye size={18} /></button>
              <button title="Edit" data-testid={`edit-tut-${t.id}`} onClick={() => editTut(t)} className="p-2 text-stone-500 hover:text-[#121212]"><Pencil size={18} /></button>
              <button title="Duplicate" data-testid={`dup-tut-${t.id}`} onClick={() => duplicateTut(t.id)} className="p-2 text-stone-500 hover:text-[#121212]"><Copy size={18} /></button>
              <button title={t.published !== false ? "Move to draft" : "Publish"} data-testid={`pub-tut-${t.id}`} onClick={() => toggleTutPublished(t)} className="p-2 text-stone-500 hover:text-[#121212]"><FileText size={18} /></button>
              <button title="Delete" data-testid={`del-tut-${t.id}`} onClick={() => delTut(t.id)} className="p-2 text-stone-500 hover:text-red-500"><Trash2 size={18} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
